import { useEffect } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import { isBedbankOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getSearchTileHasLoyaltyPackageUpgrade from 'loyalty/selectors/getSearchTileHasLoyaltyPackageUpgrade'
import {
  getCanBookAnyPropertyViaAccommodationV2,
  getIsAccommodationV2BookingForProperty,
} from 'selectors/accommodationV2Selectors'

interface Params {
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants> | undefined
  /** When `false`, the banner can't render so no network calls are made. */
  enabled?: boolean
}

export interface SearchPropertyRateLoyaltyPackageUpgradeStatus {
  available: boolean
  /** Eligible but still resolving — gate the tile's reveal on this to avoid a late banner pop-in. */
  fetching: boolean
}

/**
 * Whether the loyalty package-upgrade banner should show on a dated SPP/LPC or bedbank/LPP search tile,
 * and whether that's still resolving. Ineligible tiles make no network calls.
 *
 * Bedbank mirrors `BedbankOfferPage`'s `shouldUseSpp`: show only for a valid bedbank-property mapping —
 * otherwise the banner would be a dead click-through to a page with no upgrade UI. Promotional offers
 * used to need the `spp_bedbank_promos` experiment; that concluded in HP-2732, so they always take the
 * SPP path now.
 */
function useSearchPropertyRateLoyaltyPackageUpgrade(
  offer: App.AnyOffer | undefined,
  { checkIn, checkOut, rooms, enabled = true }: Params,
): SearchPropertyRateLoyaltyPackageUpgradeStatus {
  const dispatch = useAppDispatch()

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const hasUpgradeBenefit = useUserHasLoyaltyBenefit('hotel_upgrade')
  const isTargetPackageUpgradesEnabled = useAppSelector(
    (state) => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltySppPackageUpgrades],
  )

  const isBedbank = isBedbankOffer(offer)
  const isSupportedOffer = isLpcHotelOffer(offer) || isBedbank
  const isDated = !!(checkIn && checkOut && rooms?.length)
  // An upgrade can only be applied to a single-room booking, so there is nothing to advertise once a
  // second room is added — the offer page hides its own chip in `getHasUpgradeableRate`, and clicking
  // through to it would find no upgrade to apply. Gating here also skips the fetches below.
  const isSingleRoom = rooms?.length === 1

  // Gates the mapping fetch; the async bedbank mapping-validity check is applied afterwards.
  const isCandidate =
    enabled &&
    isLoyaltyEnabled &&
    isCurrentRegionUserLoyaltyRegion &&
    hasUpgradeBenefit &&
    isTargetPackageUpgradesEnabled &&
    isDated &&
    isSingleRoom &&
    isSupportedOffer

  const offerType = isBedbank ? OFFER_TYPE_BED_BANK : OFFER_TYPE_ALWAYS_ON
  const [mapping, mappingFetching] = usePropertyOfferMapping(offerType, offer?.id ?? '', { disabled: !isCandidate })
  const propertyId = mapping?.propertyId

  const hasValidMapping = isBedbank ? !!mapping?.bedbankPropertyIds?.includes(offer?.id ?? '') : !!propertyId
  const isEligible = isCandidate && hasValidMapping && !!propertyId

  // The accommodationV2 checkout path can't honour an upgrade yet, so don't advertise one the offer
  // page will hide. Only load the property content that decides it when this visitor could take
  // that path at all. Until that content has loaded (still fetching, or the fetch failed), hide the
  // banner rather than promise an upgrade that may not be honoured.
  const canBookViaAccommodationV2 = useAppSelector(getCanBookAnyPropertyViaAccommodationV2)
  const shouldCheckAccommodationV2 = isEligible && canBookViaAccommodationV2
  const [property, propertyFetching] = useProperty(propertyId, { disabled: !shouldCheckAccommodationV2 })
  const accommodationV2Pending = shouldCheckAccommodationV2 && propertyFetching
  const isAccommodationV2Booking = useAppSelector(
    (state) => shouldCheckAccommodationV2 && getIsAccommodationV2BookingForProperty(state, propertyId),
  )
  const isHiddenForAccommodationV2 = shouldCheckAccommodationV2 && (!property || isAccommodationV2Booking)

  const ratesKey =
    isEligible && propertyId && checkIn && checkOut
      ? buildPropertyRatesKey({
          propertyId,
          checkIn,
          checkOut,
          occupancies: rooms ?? [],
          utmContent: undefined,
          roomTypeId: undefined,
          roomRateId: undefined,
        })
      : undefined

  useEffect(() => {
    if (isEligible && propertyId && checkIn && checkOut && rooms) {
      // Cached with no TTL → at most one call per dated, eligible tile.
      dispatch(
        fetchPropertyRates(propertyId, {
          checkIn,
          checkOut,
          occupancies: rooms,
          utmContent: undefined,
          roomTypeId: undefined,
          roomRateId: undefined,
        }),
      )
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ratesKey])

  const ratesPending = useAppSelector((state) => {
    if (!ratesKey) return false
    const record = state.offer.propertyRates[ratesKey]
    return !record || !!record.fetching
  })

  const available = useAppSelector(
    (state) =>
      isEligible &&
      !isHiddenForAccommodationV2 &&
      getSearchTileHasLoyaltyPackageUpgrade(state, {
        propertyId,
        checkIn,
        checkOut,
        occupancies: rooms ?? [],
      }),
  )

  // Hold the tile's reveal while a candidate is still resolving (mapping fetching, rates pending, or
  // the accommodationV2 check still loading property content).
  const fetching = isCandidate && (mappingFetching || (isEligible && (ratesPending || accommodationV2Pending)))

  return { available, fetching }
}

export default useSearchPropertyRateLoyaltyPackageUpgrade
